import type {QueryFunction, QueryKey, QueryMeta} from '@tanstack/query-core';
import {useMemo} from 'react';
import {
  CreatorSearchResult,
  SearchState,
} from '@/types/models/creator-crm/creatorCrm';
import {Paging} from '@/types/models/creator-crm/creatorCrmStore';
import {FiltersGroup} from '@/types/models/creator-crm/filter';
import {FilterIds, FiltersGroupId} from '@/types/models/creator-crm/filterId';
import {SortingState} from '@/types/models/creator-crm/sorting';
import QueryContext from '@/api/QueryContext';
import searchCreatorsFetchers, {
  CreatorsSearchResponse,
  RequestType,
} from '@/api/fetchers/creators/search-crm/searchCreatorCrm';
import {
  mapToAppliedFilters,
  mapToMultiplatformFilter,
  mapToSorting,
} from '@/api/fetchers/creators/search-crm/utils';
import useGuardedFetchNextPage from '@/hooks/use-guarded-fetch-next-page';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';

const DEFAULT_CREATORS_SEARCH_RESPONSE: CreatorsSearchResponse = {
  searchSessionId: undefined,
  creators: [],
  loadedPages: 0,
  pagingParams: {
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
  },
};

const BASIC_QUERY_KEY = 'searchCreatorCrm';

export type SearchCreatorCrmQueryProps = {
  sessionId?: string;
  paging: Paging;
  searchState?: SearchState;
  sorting?: SortingState;
  selectedBrandId: string;
  filterGroups: Record<FiltersGroupId, FiltersGroup<FilterIds>>;
  isRestoring: boolean;
  pageParam?: number;
  disabled?: boolean;
  crm?: boolean;
};

function isStartSearchSessionRequest({sessionId}: SearchCreatorCrmQueryProps) {
  return !sessionId;
}

export function mapStateToQueryKey(
  props: SearchCreatorCrmQueryProps
): QueryKey {
  const {paging, searchState, sorting, filterGroups, selectedBrandId, crm} =
    props;

  return [
    BASIC_QUERY_KEY,
    mapToSorting(sorting),
    mapToAppliedFilters(filterGroups),
    searchState,
    selectedBrandId,
    paging.itemsPerPage,
    crm,
  ];
}

function mapStateToQueryFunction(
  props: SearchCreatorCrmQueryProps
): QueryFunction<CreatorsSearchResponse> {
  return isStartSearchSessionRequest(props)
    ? searchCreatorsFetchers.startSearchSession
    : searchCreatorsFetchers.changePage;
}

function mapStateToQueryMeta(props: SearchCreatorCrmQueryProps): QueryMeta {
  const {
    sessionId,
    paging,
    searchState,
    sorting,
    filterGroups,
    selectedBrandId,
  } = props;
  const vettingMeta = {
    brandId: selectedBrandId,
  };

  if (isStartSearchSessionRequest(props)) {
    return {
      type: 'multiplatform' as RequestType,
      sort: mapToSorting(sorting),
      query: mapToMultiplatformFilter(filterGroups, searchState),
      itemsPerPage: paging.itemsPerPage,
      crm: props.crm,
      ...vettingMeta,
    };
  }
  return {sessionId};
}

function useSearchCreatorCrmQuery(props: SearchCreatorCrmQueryProps) {
  const {disabled = false} = props;
  const response = useInfiniteReactQuery<CreatorsSearchResponse>({
    queryKey: mapStateToQueryKey(props),
    queryFn: (context: QueryContext) => mapStateToQueryFunction(props)(context),
    keepPreviousData: false,
    enabled: !props.isRestoring && !disabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    meta: mapStateToQueryMeta(props),
    getNextPageParam: (lastPage: CreatorsSearchResponse) => {
      if (!props.sessionId) return undefined;
      const {pagingParams} = lastPage;
      return pagingParams.currentPage < pagingParams.totalPages
        ? pagingParams.currentPage + 1
        : undefined;
    },
  });

  const creatorsSearchResponse: CreatorsSearchResponse = useMemo(() => {
    const {pages = []} = response?.data || {pages: []};
    const [firstPageData] = pages;

    const responseData = firstPageData
      ? {
          searchSessionId: firstPageData.searchSessionId,
          creators: pages.flatMap(
            (pageData: {creators: CreatorSearchResult[]}) => pageData?.creators
          ),
          pagingParams: firstPageData.pagingParams,
          loadedPages: pages.length,
        }
      : null;
    return responseData || {...DEFAULT_CREATORS_SEARCH_RESPONSE};
  }, [response.data]);

  const fetchNextPageGuarded = useGuardedFetchNextPage(
    response.fetchNextPage,
    response.isFetching || response.isLoading
  );

  return {
    data: creatorsSearchResponse,
    isError: response.isError,
    isFetching: response.isFetching,
    isRefetching: response.isRefetching,
    isLoading: response.isLoading,
    isFetched: response.isFetched,
    refetch: response.refetch,
    fetchNextPage: fetchNextPageGuarded,
    hasNextPage: response.hasNextPage,
  };
}

export default useSearchCreatorCrmQuery;
